import React, { useEffect, useRef, useState } from 'react'
import { Avatar, Button, Checkbox, Empty, Input, InputNumber, Modal, Space, Table, Tag } from 'antd'
import type { InputRef } from 'antd'
import { ArrowLeftOutlined, PlusOutlined, ShoppingOutlined } from '@ant-design/icons'
import i from '../../../translations/i'
import { getPrice } from '../../../helpers/price'
import { openApi } from '../../../helpers/request'
import { storeSession } from '../../../helpers/stores'
import { fromDisplay } from './carts.helpers'

const PAGE_SIZE = 8

type ModifierPayload = { group_id: number, modifier_id: number, count: number }

interface PickerProps {
    open: boolean
    warehouseId: number | null
    onClose: () => void
    /** quantity — в шагах товара; true — товар лёг в корзину */
    onAdd: (product: any, quantity: number, modifiers: ModifierPayload[]) => Promise<boolean>
}

const unitName = (product: any): string => product.unit?.name
    ?? storeSession.get('project_units')?.find((unit: any) => unit.id === product.unit_id)?.name
    ?? ''

const stepOf = (product: any): number => product.step && product.step > 0 ? product.step : 1

const modifierKey = (groupId: number, modifierId: number) => `${groupId}:${modifierId}`

/**
 * Модификаторы с количеством по умолчанию — так же их предвыбирает приложение.
 * Ключ — «группа:модификатор», потому что один модификатор может жить в нескольких группах.
 */
const defaultModifiers = (product: any): Record<string, number> => {
    const counts: Record<string, number> = {}

    ;(product.modifiers_groups ?? []).forEach((group: any) => {
        ;(group.modifiers ?? []).forEach((modifier: any) => {
            if (modifier.default_count > 0) counts[modifierKey(group.id, modifier.id)] = modifier.default_count
        })
    })

    return counts
}

/** Обязательные группы, где ещё не набран минимум. Окончательно правила проверит сервер */
const unmetGroups = (product: any, counts: Record<string, number>): string[] => (product.modifiers_groups ?? [])
    .filter((group: any) => {
        const min = Math.max(group.required ? 1 : 0, group.min_count ?? 0)
        const picked = (group.modifiers ?? []).reduce((sum: number, modifier: any) => sum + (counts[modifierKey(group.id, modifier.id)] ?? 0), 0)

        return picked < min
    })
    .map((group: any) => group.name)

const ModifiersForm = ({ product, counts, setCount }: {
    product: any
    counts: Record<string, number>
    setCount: (key: string, value: number) => void
}) => (
    <div className="cart-picker__groups">
        {(product.modifiers_groups ?? []).map((group: any) => (
            <div key={group.id} className="cart-picker__group">
                <div className="cart-picker__group-title">
                    <b>{group.name}</b>
                    {group.required && <Tag color="red" className="carts-tag">{i.t('carts:picker.required')}</Tag>}
                    {(group.min_count > 0 || group.max_count > 0) && (
                        <span className="carts-muted">{i.t('carts:picker.range', { min: group.min_count ?? 0, max: group.max_count > 0 ? group.max_count : '∞' })}</span>
                    )}
                </div>
                {(group.modifiers ?? []).map((modifier: any) => {
                    const key = modifierKey(group.id, modifier.id)
                    const value = counts[key] ?? 0
                    const max = modifier.max_count > 0 ? modifier.max_count : 99

                    return (
                        <div key={key} className="cart-picker__modifier">
                            <span className="cart-picker__modifier-name">
                                {modifier.name}
                                {modifier.price > 0 && <span className="carts-muted"> +{getPrice(modifier.price)}</span>}
                                {modifier.free_count > 0 && <span className="carts-muted"> · {i.t('carts:picker.free', { n: modifier.free_count })}</span>}
                            </span>
                            {modifier.countable === false
                                ? <Checkbox checked={value > 0} onChange={(e) => setCount(key, e.target.checked ? 1 : 0)} />
                                : <InputNumber size="small" min={0} max={max} value={value} style={{ width: 80 }} onChange={(next) => setCount(key, next ?? 0)} />
                            }
                        </div>
                    )
                })}
            </div>
        ))}
    </div>
)

export const CartProductPicker = ({ open, warehouseId, onClose, onAdd }: PickerProps) => {
    const searchRef = useRef<InputRef>(null)
    const [query, setQuery] = useState('')
    const [page, setPage] = useState(1)
    const [products, setProducts] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    // Количество в единицах товара, как его видит менеджер. Ключ — id товара
    const [quantities, setQuantities] = useState<Record<number, number>>({})
    // Товар с модификаторами, для которого открыт выбор модификаторов
    const [configuring, setConfiguring] = useState<any>(null)
    const [modifierCounts, setModifierCounts] = useState<Record<string, number>>({})

    useEffect(() => {
        if (!open) return

        const timer = setTimeout(() => {
            const search = query.trim()
            let uri = `/products/select?with[]=image&with[]=unit&with[]=modifiers_groups&limit=${PAGE_SIZE}&page=${page}`
            if (/^\d+$/.test(search)) uri += `&filter[product_id]=${search}`
            else if (search !== '') uri += `&filter[name]=${encodeURIComponent(search)}`
            if (warehouseId) uri += `&warehouse_id=${warehouseId}`

            setLoading(true)
            openApi('get', uri, {}, undefined, true)
                .then(result => {
                    setProducts(result.data ?? [])
                    setTotal(Number(result.total ?? 0))
                })
                .catch(err => console.log(err))
                .finally(() => setLoading(false))
        }, 300)

        return () => clearTimeout(timer)
    }, [open, query, page, warehouseId])

    useEffect(() => {
        if (!open) setConfiguring(null)
    }, [open])

    const quantityOf = (product: any) => quantities[product.id] ?? stepOf(product)

    const add = (product: any, modifiers: ModifierPayload[] = []) => {
        setSaving(true)
        onAdd(product, fromDisplay(quantityOf(product), stepOf(product)), modifiers)
            .then((added) => {
                if (!added) return

                setQuantities((prev) => {
                    const next = { ...prev }
                    delete next[product.id]
                    return next
                })
                setConfiguring(null)
            })
            .finally(() => setSaving(false))
    }

    const startAdd = (product: any) => {
        if ((product.modifiers_groups ?? []).length > 0) {
            setModifierCounts(defaultModifiers(product))
            setConfiguring(product)
            return
        }

        add(product)
    }

    const submitModifiers = () => {
        const modifiers: ModifierPayload[] = []

        ;(configuring.modifiers_groups ?? []).forEach((group: any) => {
            ;(group.modifiers ?? []).forEach((modifier: any) => {
                const count = modifierCounts[modifierKey(group.id, modifier.id)] ?? 0
                if (count > 0) modifiers.push({ group_id: group.id, modifier_id: modifier.id, count })
            })
        })

        add(configuring, modifiers)
    }

    const columns = [
        {
            key: 'image',
            width: 56,
            render: (text: any, product: any) => (
                <Avatar shape="square" size={44} src={product.image?.path} icon={product.image ? undefined : <ShoppingOutlined />} className="carts-thumb" />
            )
        },
        {
            title: i.t('carts:picker.product'),
            key: 'name',
            render: (text: any, product: any) => (
                <div>
                    <div className="cart-item__name">{product.name}</div>
                    <Space size={4} wrap>
                        <span className="carts-muted">#{product.id}</span>
                        {product.vendor_code && <span className="carts-muted">· {product.vendor_code}</span>}
                        {Array.isArray(product.props) && product.props.map((prop: any, index: number) => (
                            <Tag key={index} className="carts-tag">{prop.category?.name}: {prop.value}</Tag>
                        ))}
                        {Boolean(product.view_variations) && <Tag className="carts-tag">{i.t('carts:picker.variants')}</Tag>}
                        {(product.modifiers_groups ?? []).length > 0 && <Tag color="purple" className="carts-tag">{i.t('carts:picker.withModifiers')}</Tag>}
                    </Space>
                </div>
            )
        },
        {
            title: i.t('carts:picker.price'),
            key: 'price',
            width: 120,
            render: (text: any, product: any) => product.view_variations ? '' : (
                <div>
                    {product.old_price > product.price && <div className="cart-item__price-old">{getPrice(product.old_price)}</div>}
                    <div>{getPrice(product.price)}</div>
                </div>
            )
        },
        {
            key: 'actions',
            width: 230,
            render: (text: any, product: any) => {
                // Родителя с вариациями в корзину не положить — только конкретную вариацию
                if (product.view_variations) return <span className="carts-muted">{i.t('carts:picker.chooseVariant')}</span>

                const step = stepOf(product)

                return (
                    <Space.Compact>
                        <InputNumber
                            min={step}
                            step={step}
                            value={quantityOf(product)}
                            style={{ width: 100 }}
                            addonAfter={unitName(product) || undefined}
                            onChange={(value) => setQuantities((prev) => ({ ...prev, [product.id]: value ?? step }))}
                        />
                        <Button type="primary" icon={<PlusOutlined />} loading={saving} onClick={() => startAdd(product)}>
                            {i.t('carts:picker.add')}
                        </Button>
                    </Space.Compact>
                )
            }
        },
    ]

    const unmet = configuring ? unmetGroups(configuring, modifierCounts) : []

    return (
        <Modal
            open={open}
            width={920}
            onCancel={onClose}
            // autoFocus внутри Modal перебивает его собственный фокус — ставим вручную после открытия
            afterOpenChange={(visible) => { if (visible) searchRef.current?.focus() }}
            title={configuring ? `${i.t('carts:picker.modifiers')}: ${configuring.name}` : i.t('carts:picker.title')}
            footer={configuring
                ? [
                    <Button key="back" icon={<ArrowLeftOutlined />} onClick={() => setConfiguring(null)}>{i.t('carts:picker.back')}</Button>,
                    <Button key="add" type="primary" loading={saving} disabled={unmet.length > 0} onClick={submitModifiers}>
                        {i.t('carts:picker.addToCart')}
                    </Button>,
                ]
                : [<Button key="done" onClick={onClose}>{i.t('carts:picker.done')}</Button>]
            }
        >
            {configuring
                ? <>
                    <ModifiersForm
                        product={configuring}
                        counts={modifierCounts}
                        setCount={(key, value) => setModifierCounts((prev) => ({ ...prev, [key]: value }))}
                    />
                    {unmet.length > 0 && <div className="cart-picker__unmet">{i.t('carts:picker.unmet', { groups: unmet.join(', ') })}</div>}
                </>
                : <>
                    <Input.Search
                        ref={searchRef}
                        allowClear
                        placeholder={i.t('carts:picker.search')}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value)
                            setPage(1)
                        }}
                        className="cart-picker__search"
                    />
                    <Table
                        rowKey="id"
                        size="small"
                        columns={columns}
                        dataSource={products}
                        loading={loading}
                        locale={{ emptyText: <Empty description={i.t('carts:picker.empty')} /> }}
                        pagination={{
                            current: page,
                            pageSize: PAGE_SIZE,
                            total,
                            hideOnSinglePage: true,
                            showSizeChanger: false,
                            onChange: (next) => setPage(next),
                        }}
                    />
                </>
            }
        </Modal>
    )
}
